import {Component, Input, OnDestroy, OnInit} from "@angular/core";
import {
    Aangifte, BokkzAlgDataService, IncomeTaxEntity, OmschrijvingEnBedrag,
    Onderneming, Ondernemingsvorm
} from '@snelstart-ventures/fiscaal/domain-logic';
import {FormArray, FormBuilder, FormGroup} from "@angular/forms";
import { SelectFormControlItem, SharedUiFormControlsModule } from '@snelstart/ui-form-controls';
import {BokkzIbDataService} from "@snelstart-ventures/fiscaal/domain-logic";
import {BehaviorSubject, filter, Subscription} from "rxjs";
import {Event, NavigationEnd, Router} from "@angular/router";
import { SharedUiDetailsModule } from '@snelstart/ui-details';
import { SharedUiButtonModule } from '@snelstart/ui-button';
import { SharedUiDialogModule } from '@snelstart/ui-dialog';
import { CommonModule } from '@angular/common';

@Component({
  selector: 'snelstart-venture-fiscaal-feature-vragen-vooraf-ond',
  templateUrl: './vragen-vooraf-ond.component.html',
  styleUrls: ['./vragen-vooraf-ond.component.scss'],
  imports: [
    SharedUiDetailsModule,
    SharedUiFormControlsModule,
    SharedUiButtonModule,
    SharedUiDialogModule,
    CommonModule
  ]
})
export class VragenVoorafOndComponent implements OnInit, OnDestroy {

    @Input() public id!: string;
    public clientId!: string;

    private focusedElementId: string | null = null;
    private routerSubscription: Subscription | undefined;
    private incomeTaxEntitySubscription: Subscription | undefined;
    private clientEntitySubscription: Subscription | undefined;
    private isDefAangifteSubscription: Subscription | undefined;
    private isSaving = false;

    public incomeTaxEntity = {} as IncomeTaxEntity;

    public partnerBoolean = {} as boolean;
    @Input() partner = new BehaviorSubject<boolean>(false);

    public isDefAangifte = {} as boolean;
    public dialogVisible = false;
    public showSpinner = true;
    public ondernemingFG = {} as FormGroup;

    public index = 0;

    activeHelp: string | null = null;

    public optionsJaNee: SelectFormControlItem<boolean>[] = [
        {value: false, label: "Nee"},
        {value: true, label: "Ja"},
    ]

    public optionsJaNeeAgra: SelectFormControlItem<boolean>[] = [
        {value: false, label: "Nee"},
        {value: true, label: "Ja (nog niet beschikbaar)", disabled: true},
    ]

    showDialog() {
        this.dialogVisible = true;
    }

    constructor(private fb: FormBuilder, private router: Router, public dataService: BokkzIbDataService, private dataAlgService: BokkzAlgDataService) {
        this.ondernemingFG = this.fb.group({
            ondernemingen: this.fb.array([
                this.createOndernemingFormGroup()])
        });
    }

    formStructure: any;

    get ondernemingen(): FormArray {
        return this.ondernemingFG.get('ondernemingen') as FormArray;
    }

    createOndernemingFormGroup(onderneming?: Onderneming) {
        return this.dataService.createOndernemingFormGroup(onderneming);
    }

    getSchadeloosstelling(index: number): FormArray {
        const outerGroup = this.ondernemingen.at(index) as FormGroup;
        return outerGroup.get('schadeloosstellingBuitenWinst') as FormArray;
    }

    addSchadeloosstelling(index: number): void {
        const innerGroup = this.fb.group<OmschrijvingEnBedrag>({
            omschrijving: '',
            bedrag: undefined,
        });
        const outerGroup = this.ondernemingen.at(index) as FormGroup;
        const innerArray = outerGroup.get('schadeloosstellingBuitenWinst') as FormArray;
        innerArray.push(innerGroup);
    }

    verwijderSchadeloosstelling(outerIndex: number, innerIndex: number): void {
        const innerArray = this.getSchadeloosstelling(outerIndex);
        innerArray.removeAt(innerIndex);
        this.saveForm()
    }

    public createSchadeloosstellingBuitenWinstFormGroup(schadeloosstellingBuitenWinst?: OmschrijvingEnBedrag): FormGroup {
        return this.fb.group(({
            omschrijving: schadeloosstellingBuitenWinst ? schadeloosstellingBuitenWinst.omschrijving : undefined,
            bedrag: schadeloosstellingBuitenWinst ? schadeloosstellingBuitenWinst.bedrag : undefined
        } as OmschrijvingEnBedrag))
    }

    public ngOnInit(): void {
        this.isDefAangifteSubscription = this.dataService.isDefAangifte.subscribe((isDefAangifte) => {
            this.isDefAangifte = isDefAangifte;
        });
        this.incomeTaxEntitySubscription = this.dataService.incomeTaxEntity.subscribe((incomeTaxEntity) => {
            this.incomeTaxEntity = incomeTaxEntity;
            if (incomeTaxEntity.aangifte) {
                const aangifte = incomeTaxEntity.aangifte as Aangifte;
                this.updateForm();
                this.partner.subscribe((boolean) => {
                    this.partnerBoolean = boolean;
                    this.ondernemingFG.reset()
                    this.updateForm();
                });
            }
        });
        this.clientEntitySubscription = this.dataAlgService.client.subscribe((client) => {
            this.clientId = client.sstRelationId!;
        })
        this.routerSubscription = this.router.events.pipe(filter((event: Event): event is NavigationEnd => event instanceof NavigationEnd))
            .subscribe((event: NavigationEnd) => {
                this.handleUrlChange(event.urlAfterRedirects);
            });
        this.partner.subscribe((boolean) => {
            this.partnerBoolean = boolean;
            this.ondernemingFG.reset()
            this.updateForm();
        });
    }

    ngOnDestroy(): void {
        if (this.routerSubscription) {
            this.routerSubscription.unsubscribe(); // Unsubscribe to avoid memory leaks
        }
        if (this.incomeTaxEntitySubscription) {
            this.incomeTaxEntitySubscription.unsubscribe();
        }
        if (this.clientEntitySubscription) {
            this.clientEntitySubscription.unsubscribe();
        }
        if (this.isDefAangifteSubscription) {
            this.isDefAangifteSubscription.unsubscribe();
        }
    }

    handleUrlChange(newUrl: string) {
        if (!this.isSaving && this.hasUnsavedChanges()) {
            this.isSaving = true;
            this.dataService.postIncomeTaxEntity(this.clientId, false, this.incomeTaxEntity);
            this.isSaving = false;
        }
    }

    private hasUnsavedChanges(): boolean {
        return this.ondernemingFG.dirty
    }

    private updateForm(): void {
        let ondernemingenLijst = this.incomeTaxEntity.aangifte?.box1?.winstUitOnderneming?.ondernemingen || [];
        if (this.partnerBoolean && this.incomeTaxEntity.aangifte?.partnerAangifte) {
            ondernemingenLijst = this.incomeTaxEntity.aangifte?.partnerAangifte?.box1?.winstUitOnderneming?.ondernemingen || [];
        }

        const formArray = this.ondernemingFG.get('ondernemingen') as FormArray;
        const focusedControl = this.focusedElementId ? document.getElementById(this.focusedElementId) as HTMLElement : null;

        while (formArray.length > ondernemingenLijst.length) {
            formArray.removeAt(formArray.length - 1);
        }

        // Update ondernemingen
        for (let i = 0; i < ondernemingenLijst.length; i++) {
            if (i < formArray.length) {
                const control = formArray.at(i) as FormGroup;
                control.patchValue(ondernemingenLijst[i], {emitEvent: false});
            } else {
                const formGroup = this.createOndernemingFormGroup(ondernemingenLijst[i])
                formArray.push(formGroup);
            }
        }
        if (focusedControl && document.contains(focusedControl)) {
            focusedControl.focus();
        }

        // Update schadeloosstelling
        for (let i = 0; i < ondernemingenLijst.length; i++) {
            const onderneming = ondernemingenLijst[i];
            const schadeloosstellingBuitenWinst = onderneming.schadeloosstellingBuitenWinst || [];
            const schadeloosstellingBuitenWinstFormArray = this.getSchadeloosstelling(i);

            while (schadeloosstellingBuitenWinstFormArray.length > schadeloosstellingBuitenWinstFormArray.length) {
                schadeloosstellingBuitenWinstFormArray.removeAt(schadeloosstellingBuitenWinstFormArray.length - 1);
            }
            for (let s = 0; s < schadeloosstellingBuitenWinst.length; s++) {
                if (s < schadeloosstellingBuitenWinstFormArray.length) {
                    const control = schadeloosstellingBuitenWinstFormArray.at(s) as FormGroup;
                    control.patchValue(schadeloosstellingBuitenWinst[s]);
                } else {
                    const schadeloosstelling = this.createSchadeloosstellingBuitenWinstFormGroup(schadeloosstellingBuitenWinst[s]);
                    schadeloosstellingBuitenWinstFormArray.push(schadeloosstelling);
                }
            }
        }
    }

    saveForm(): void {
        this.ondernemingFG.updateValueAndValidity();
        setTimeout(() => {
            if (!this.partnerBoolean) {
                this.incomeTaxEntity.aangifte = {
                    ...this.incomeTaxEntity.aangifte,
                    box1: {
                        ...this.incomeTaxEntity.aangifte?.box1,
                        winstUitOnderneming: {
                            ...this.incomeTaxEntity.aangifte?.box1?.winstUitOnderneming,
                            ondernemingen: this.ondernemingFG.getRawValue().ondernemingen
                        }
                    }
                } as Aangifte;
                this.dataService.updateIncomeTaxEntity(this.incomeTaxEntity);
            } else {
                this.incomeTaxEntity.aangifte = {
                    ...this.incomeTaxEntity.aangifte,
                    partnerAangifte: {
                        ...this.incomeTaxEntity.aangifte?.partnerAangifte,
                        box1: {
                            ...this.incomeTaxEntity.aangifte?.partnerAangifte?.box1,
                            winstUitOnderneming: {
                                ...this.incomeTaxEntity.aangifte?.partnerAangifte?.box1?.winstUitOnderneming,
                                ondernemingen: this.ondernemingFG.getRawValue().ondernemingen
                            }
                        }
                    },
                } as Aangifte;
                this.dataService.updateIncomeTaxEntity(this.incomeTaxEntity);
            }
        }, 0)
    }

    private getRelevantAangifte(): Aangifte | undefined {
        return this.partnerBoolean
            ? this.incomeTaxEntity.aangifte?.partnerAangifte
            : this.incomeTaxEntity.aangifte;
    }

    private getOndernemingen(): Onderneming[] | undefined {
        const aangifte = this.getRelevantAangifte();
        return aangifte?.box1?.winstUitOnderneming?.ondernemingen;
    }

    public heeftOndernemingen(): boolean {
        const ondernemingen = this.getOndernemingen();
        return !!ondernemingen && ondernemingen.length > 0;
    }

    showHelp(id: string): void {
        this.activeHelp = id;
    }

    hideHelp(): void {
        this.activeHelp = null;
    }

}



